import * as React from 'react';
import { cn } from '@/utils';
import { formatDate } from '@/utils/formatters/date.formatters';
import type { SubscriptionState } from '@/services/api/accountTypes';

/**
 * Shared surfaces for the account console. Kept in one file so every section
 * speaks the same visual language: hairline-bordered panels, definition rows,
 * tabular money, and one status vocabulary.
 */

// ───────────────────────────── formatting ─────────────────────────────

/** Money in MAJOR units (plan prices, upcoming invoice totals). */
export function formatMoney(amount: number | null | undefined, currency = 'cad'): string {
  if (amount === null || amount === undefined || Number.isNaN(amount)) return '—';
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: (currency || 'cad').toUpperCase(),
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(amount);
}

/** Money in MINOR units (Stripe invoice amounts arrive as cents). */
export function formatMinorMoney(amount: number | null | undefined, currency = 'cad'): string {
  if (amount === null || amount === undefined || Number.isNaN(amount)) return '—';
  return formatMoney(amount / 100, currency);
}

export function formatDay(value: string | null | undefined): string {
  return formatDate(value, '—', 'MMM d, yyyy');
}

/** Whole days from now until `value`; negative once it has passed. */
export function daysUntil(value: string | null | undefined): number | null {
  if (!value) return null;
  const target = new Date(value).getTime();
  if (Number.isNaN(target)) return null;
  return Math.ceil((target - Date.now()) / 86_400_000);
}

// ─────────────────────────────── status ───────────────────────────────

export type StatusTone = 'good' | 'warn' | 'bad' | 'neutral';

export const SUBSCRIPTION_TONE: Record<SubscriptionState, StatusTone> = {
  active: 'good',
  trial: 'neutral',
  past_due: 'warn',
  suspended: 'bad',
  cancelled: 'bad',
};

export const SUBSCRIPTION_LABEL: Record<SubscriptionState, string> = {
  active: 'Active',
  trial: 'Trial',
  past_due: 'Payment overdue',
  suspended: 'Suspended',
  cancelled: 'Cancelled',
};

const TONE_CLASS: Record<StatusTone, string> = {
  good: 'text-success border-success/30 bg-success/10',
  warn: 'text-warning border-warning/40 bg-warning/10',
  bad: 'text-destructive border-destructive/30 bg-destructive/10',
  neutral: 'text-brand-cyan border-brand-cyan/30 bg-brand-cyan/10',
};

const DOT_CLASS: Record<StatusTone, string> = {
  good: 'bg-success',
  warn: 'bg-warning',
  bad: 'bg-destructive',
  neutral: 'bg-brand-cyan',
};

export function StatusPill({
  tone = 'neutral',
  children,
  className,
}: {
  tone?: StatusTone;
  children: React.ReactNode;
  className?: string;
}) {
  return (
    <span
      className={cn(
        'inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-xs font-semibold',
        TONE_CLASS[tone],
        className
      )}
    >
      <span className={cn('h-1.5 w-1.5 rounded-full', DOT_CLASS[tone])} aria-hidden="true" />
      {children}
    </span>
  );
}

// ─────────────────────────────── surfaces ──────────────────────────────

export function Panel({
  title,
  description,
  action,
  children,
  className,
  bodyClassName,
}: {
  title?: React.ReactNode;
  description?: React.ReactNode;
  action?: React.ReactNode;
  children?: React.ReactNode;
  className?: string;
  bodyClassName?: string;
}) {
  return (
    <section
      className={cn(
        'rounded-xl border border-border bg-card text-card-foreground',
        'shadow-[0_1px_2px_rgba(15,23,42,0.04),0_10px_30px_-18px_rgba(15,23,42,0.25)]',
        className
      )}
    >
      {(title || action) && (
        <header className="flex flex-wrap items-start justify-between gap-3 border-b border-border px-5 py-4">
          <div className="min-w-0">
            {title && <h2 className="text-base font-semibold tracking-tight">{title}</h2>}
            {description && (
              <p className="mt-1 max-w-[65ch] text-sm text-muted-foreground">{description}</p>
            )}
          </div>
          {action && <div className="flex shrink-0 items-center gap-2">{action}</div>}
        </header>
      )}
      <div className={cn('px-5 py-4', bodyClassName)}>{children}</div>
    </section>
  );
}

/** Label/value pair. Values that are data (money, dates, ids) use mono. */
export function DataRow({
  label,
  value,
  hint,
  mono,
  action,
}: {
  label: React.ReactNode;
  value: React.ReactNode;
  hint?: React.ReactNode;
  mono?: boolean;
  action?: React.ReactNode;
}) {
  return (
    <div className="flex flex-wrap items-baseline justify-between gap-x-6 gap-y-1 border-b border-border/60 py-3 last:border-0">
      <dt className="text-sm text-muted-foreground">{label}</dt>
      {/* Wraps, and breaks a long value such as an email or id, so a row
          never runs past the panel on a phone (T101026002). */}
      <dd className="flex min-w-0 flex-wrap items-center gap-3">
        <span
          className={cn(
            'min-w-0 text-sm font-medium text-foreground [overflow-wrap:anywhere]',
            mono && 'font-mono tabular-nums tracking-tight'
          )}
        >
          {value}
        </span>
        {hint && <span className="text-xs text-muted-foreground">{hint}</span>}
        {action}
      </dd>
    </div>
  );
}

export function SectionTitle({
  title,
  description,
  action,
}: {
  title: string;
  description?: string;
  action?: React.ReactNode;
}) {
  return (
    <div className="mb-6 flex flex-wrap items-end justify-between gap-4">
      <div>
        <h1 className="text-2xl font-semibold tracking-tight">{title}</h1>
        {description && (
          <p className="mt-2 max-w-[65ch] text-sm text-muted-foreground">{description}</p>
        )}
      </div>
      {action}
    </div>
  );
}

export function EmptyState({
  icon: Icon,
  title,
  body,
  action,
}: {
  icon: React.ElementType;
  title: string;
  body?: string;
  action?: React.ReactNode;
}) {
  return (
    <div className="flex flex-col items-center px-6 py-12 text-center">
      <span className="mb-4 flex h-11 w-11 items-center justify-center rounded-lg border border-border bg-muted/40">
        <Icon className="h-5 w-5 text-muted-foreground" aria-hidden="true" />
      </span>
      <p className="text-sm font-medium text-foreground">{title}</p>
      {body && <p className="mt-1 max-w-[48ch] text-sm text-muted-foreground">{body}</p>}
      {action && <div className="mt-5">{action}</div>}
    </div>
  );
}

/** Inline problem notice. Names the problem and, where possible, the recovery. */
export function Notice({
  tone = 'warn',
  title,
  children,
  action,
}: {
  tone?: StatusTone;
  title: string;
  children?: React.ReactNode;
  action?: React.ReactNode;
}) {
  return (
    <div
      className={cn(
        'flex flex-wrap items-start justify-between gap-4 rounded-lg border px-4 py-3',
        TONE_CLASS[tone]
      )}
      role={tone === 'bad' || tone === 'warn' ? 'alert' : undefined}
    >
      <div className="min-w-0">
        <p className="text-sm font-semibold">{title}</p>
        {children && <p className="mt-1 max-w-[65ch] text-sm opacity-90">{children}</p>}
      </div>
      {action && <div className="shrink-0">{action}</div>}
    </div>
  );
}

export function FieldLabel({
  htmlFor,
  children,
}: {
  htmlFor?: string;
  children: React.ReactNode;
}) {
  return (
    <label htmlFor={htmlFor} className="mb-1.5 block text-sm font-medium text-foreground">
      {children}
    </label>
  );
}

export const accountInputClass =
  'h-10 w-full rounded-lg border border-input bg-background px-3 text-sm text-foreground ' +
  'placeholder:text-muted-foreground transition-shadow ' +
  'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-cyan/60 disabled:opacity-60';

/** Skeleton row used while a panel's data loads. */
export function LoadingRows({ rows = 3 }: { rows?: number }) {
  return (
    <div className="space-y-3" aria-hidden="true">
      {Array.from({ length: rows }).map((_, index) => (
        <div key={index} className="h-4 animate-pulse rounded bg-muted" style={{ width: `${88 - index * 14}%` }} />
      ))}
    </div>
  );
}
